import { useOktaAuth } from '@okta/okta-react';
import jwt_decode from 'jwt-decode';
import { useEffect, useState, useMemo } from 'react';

import { shouldUseNativeAuth } from '@app/helpers/platform';

import { getNativeAuthBridge } from '../utils/native-auth-bridge';

export interface SafeAuthState {
  isAuthenticated: boolean;
  isPending: boolean;
  error?: string | null;
  idToken?: {
    claims?: {
      preferred_username?: string;
      [key: string]: any;
    };
  };
}

export interface SafeAuthReturn {
  oktaAuth: any; // Okta auth client (always available for operations like setOriginalUri)
  authState: SafeAuthState | null;
  isReady: boolean;
}

/**
 * Safe authentication hook that prevents useOktaAuth() errors in native contexts
 *
 * Purpose: Provide a drop-in replacement for useOktaAuth() that works safely in both contexts:
 * - Web auth: Returns useOktaAuth() values directly (no changes to working flow)
 * - Native auth: Returns safe auth state from bridge, avoiding useOktaAuth() token issues
 *
 * This prevents components from breaking when useOktaAuth() has no tokens in native contexts.
 */
// eslint-disable-next-line import/prefer-default-export
export const useSafeAuth = (): SafeAuthReturn => {
  const webAuth = useOktaAuth(); // Always call this (required by React hooks rules)
  const [nativeAuthState, setNativeAuthState] = useState<SafeAuthState>({
    isAuthenticated: false,
    isPending: true,
    error: null,
  });

  // Memoize to prevent repeated calls
  const isNativeAuth = useMemo(() => shouldUseNativeAuth(), []);

  // For native auth, get auth state via bridge
  useEffect(() => {
    const checkNativeAuth = async () => {
      if (isNativeAuth) {
        try {
          const nativeBridge = getNativeAuthBridge();
          const result = await nativeBridge.refreshTokens();

          // Decode ID token to get user info
          let idTokenClaims: any = null;
          if (result?.idToken) {
            try {
              idTokenClaims = jwt_decode(result.idToken);
            } catch (error) {
              console.warn('[useSafeAuth] Failed to decode ID token:', error);
            }
          }

          setNativeAuthState({
            isAuthenticated: !!result?.accessToken,
            isPending: false,
            error: null,
            idToken: {
              claims: idTokenClaims,
            },
          });
        } catch (error) {
          console.log('[useSafeAuth] Native auth check failed:', error);
          setNativeAuthState({
            isAuthenticated: false,
            isPending: false,
            error: error instanceof Error ? error.message : 'Authentication failed',
          });
        }
      }
    };

    checkNativeAuth();
  }, [isNativeAuth]); // Only re-run if auth type changes

  // Return appropriate implementation based on platform
  if (isNativeAuth) {
    return {
      oktaAuth: webAuth.oktaAuth, // Still needed for operations like setOriginalUri
      authState: nativeAuthState,
      isReady: !nativeAuthState.isPending,
    };
  }

  // For web auth, pass through useOktaAuth() values directly (no changes to working flow)
  const adaptedAuthState: SafeAuthState | null = webAuth.authState ? {
    isAuthenticated: webAuth.authState.isAuthenticated || false,
    isPending: webAuth.authState.isPending as boolean,
    error: webAuth.authState.error?.message || null,
    idToken: webAuth.authState.idToken,
  } : null;

  return {
    oktaAuth: webAuth.oktaAuth,
    authState: adaptedAuthState,
    isReady: !!webAuth.authState,
  };
};
